'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth/auth-context';
import { LoginModal } from './LoginModal';
import { Button } from '@/components/ui/button';
import { UserRole, InspecDoorModule, FrontendUser } from '@/types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredModule?: InspecDoorModule;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredModule,
  fallback,
  redirectTo
}) => {
  const { user, isLoading, hasRole, hasPermission } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  useEffect(() => {
    if (!isLoading && !user) {
      setShowLoginModal(true);
    }
  }, [user, isLoading]);
  
  const handleLoginSuccess = (user: FrontendUser) => {
    setShowLoginModal(false);
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Not authenticated
  if (!user) {
    return (
      <>
        {fallback || (
          <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Anmeldung erforderlich
              </h1>
              <p className="text-gray-600 mb-6">
                Bitte melden Sie sich an, um auf InspecDoor zuzugreifen.
              </p>
              <Button onClick={() => setShowLoginModal(true)}>
                Anmelden
              </Button>
            </div>
          </div>
        )}
        
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
          redirectTo={redirectTo}
        />
      </>
    );
  }
  
  // Check role permission
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Keine Berechtigung
          </h1>
          <p className="text-gray-600">
            Sie haben keine Berechtigung für diese Seite.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Erforderliche Rolle: <strong>{requiredRole}</strong><br />
            Ihre Rolle: <strong>{user.role}</strong>
          </p>
        </div>
      </div>
    );
  }
  
  // Check module permission
  if (requiredModule && !hasPermission(requiredModule)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Modul-Zugriff verweigert
          </h1>
          <p className="text-gray-600">
            Sie haben keinen Zugriff auf dieses Modul.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Modul: <strong>{requiredModule}</strong><br />
            Ihre Rolle: <strong>{user.role}</strong>
          </p>
        </div>
      </div>
    );
  }
  
  // All checks passed
  return <>{children}</>;
};
